import { Directive, ElementRef, NgZone, inject } from '@angular/core';

/**
 * Material 3 press ripple for interactive cards.
 *
 * Angular port of the ComPsych DS ripple. Apply to a positioned,
 * `overflow: hidden` element. On primary pointer-down it spawns a circle at the
 * pointer that scales out to the farthest corner and fades; keyboard activation
 * (Enter/Space) spawns a centered ripple.
 *
 * The ripple nodes are created imperatively and animated with the Web Animations
 * API, and their colour is `currentColor` — so no global stylesheet is needed
 * and the ripple inherits the host's text colour (white on filled/image, dark on
 * outlined/gradient), matching the reference.
 */
@Directive({
  selector: '[compsychRipple]',
  standalone: true,
  host: {
    '(pointerdown)': 'onPointerDown($event)',
    '(keydown)': 'onKeyDown($event)',
  },
})
export class RippleDirective {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);

  /**
   * Imperative suppression — the card surface only ripples when `interactive`
   * and not `disabled`. Set by the host component. Defaults to off.
   */
  suppressed = false;

  private static readonly DURATION_MS = 450;
  private layer: HTMLElement | null = null;

  onPointerDown(event: PointerEvent): void {
    if (this.suppressed || event.button !== 0) return;
    const rect = this.el.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const corners: Array<[number, number]> = [
      [0, 0],
      [rect.width, 0],
      [0, rect.height],
      [rect.width, rect.height],
    ];
    const maxRadius = Math.max(...corners.map(([cx, cy]) => Math.hypot(cx - x, cy - y)));
    this.spawn(x, y, maxRadius * 2);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.suppressed || event.repeat) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const rect = this.el.nativeElement.getBoundingClientRect();
    this.spawn(rect.width / 2, rect.height / 2, Math.hypot(rect.width, rect.height));
  }

  private ensureLayer(): HTMLElement {
    if (this.layer && this.layer.isConnected) return this.layer;
    const layer = document.createElement('span');
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, {
      position: 'absolute',
      inset: '0',
      overflow: 'hidden',
      borderRadius: 'inherit',
      pointerEvents: 'none',
      zIndex: '0',
    });
    this.el.nativeElement.appendChild(layer);
    this.layer = layer;
    return layer;
  }

  private spawn(x: number, y: number, size: number): void {
    if (typeof (Element.prototype as { animate?: unknown }).animate !== 'function') return;
    const layer = this.ensureLayer();
    const dot = document.createElement('span');
    Object.assign(dot.style, {
      position: 'absolute',
      left: `${x}px`,
      top: `${y}px`,
      width: `${size}px`,
      height: `${size}px`,
      marginLeft: `${-size / 2}px`,
      marginTop: `${-size / 2}px`,
      borderRadius: '50%',
      backgroundColor: 'currentColor',
      pointerEvents: 'none',
    });
    layer.appendChild(dot);

    this.zone.runOutsideAngular(() => {
      const anim = dot.animate(
        [
          { transform: 'scale(0)', opacity: 0.12 },
          { transform: 'scale(1)', opacity: 0 },
        ],
        { duration: RippleDirective.DURATION_MS, easing: 'ease-out' },
      );
      anim.onfinish = () => dot.remove();
    });
  }
}
